import { FC, RefObject, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

interface Props {
  anchorRef: RefObject<HTMLElement>;
  onDismiss: () => void;
}

interface Position {
  top: number;
  left: number;
  caretLeft: number;
}

const BUBBLE_WIDTH = 260;
const MARGIN = 8;
const GAP = 12;

const LessonCoachmark: FC<Props> = ({ anchorRef, onDismiss }) => {
  const [pos, setPos] = useState<Position | null>(null);

  useLayoutEffect(() => {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const anchorCenter = rect.left + rect.width / 2;
    const left = Math.min(Math.max(anchorCenter - BUBBLE_WIDTH / 2, MARGIN), window.innerWidth - BUBBLE_WIDTH - MARGIN);
    const caretLeft = Math.min(Math.max(anchorCenter - left, 16), BUBBLE_WIDTH - 16);
    setPos({ top: rect.top, left, caretLeft });
  }, [anchorRef]);

  if (!pos) return null;

  return createPortal(
    <div
      role="dialog"
      aria-label="New: Teacher-led resources"
      className="tw-fixed tw-z-[1000] tw-font-atkinson"
      style={{ top: pos.top, left: pos.left, width: BUBBLE_WIDTH, transform: `translateY(calc(-100% - ${GAP}px))` }}
    >
      <div className="tw-relative tw-rounded-xl tw-bg-neutral-950 tw-px-4 tw-py-3 tw-text-white tw-shadow-lg">
        <div className="tw-mb-1 tw-flex tw-items-start tw-justify-between tw-gap-3">
          <p className="tw-m-0 tw-font-sora tw-text-sm tw-font-bold tw-text-white">New: Teacher-led resources</p>
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Dismiss"
            className="tw-flex tw-cursor-pointer tw-items-center tw-border-none tw-bg-transparent tw-p-0 tw-text-white"
          >
            <X size={16} aria-hidden />
          </button>
        </div>
        <p className="tw-m-0 tw-text-sm tw-text-neutral-200">
          Every skill now comes with a teacher guide and class materials. Check them out!
        </p>
        <span
          aria-hidden
          className="tw-absolute tw-top-full tw-h-3 tw-w-3 -tw-translate-x-1/2 -tw-translate-y-1/2 tw-rotate-45 tw-bg-neutral-950"
          style={{ left: pos.caretLeft }}
        />
      </div>
    </div>,
    document.body,
  );
};

export default LessonCoachmark;
